import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '@/lib/utils';

const typographyVariants = cva('', {
  variants: {
    variant: {
      // Headings
      h1: 'scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl',
      h2: 'scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0',
      h3: 'scroll-m-20 text-2xl font-semibold tracking-tight',
      h4: 'scroll-m-20 text-xl font-semibold tracking-tight',
      h5: 'scroll-m-20 text-lg font-semibold tracking-tight',
      h6: 'scroll-m-20 text-base font-semibold tracking-tight',

      // Body text
      p: 'leading-7 [&:not(:first-child)]:mt-6',
      body: 'text-sm leading-relaxed',
      'body-sm': 'text-xs leading-relaxed',
      'body-lg': 'text-base leading-relaxed',

      // Display text
      display: 'text-5xl font-bold tracking-tight lg:text-6xl',
      'display-sm': 'text-4xl font-bold tracking-tight lg:text-5xl',

      // Utility variants
      lead: 'text-xl text-muted-foreground',
      large: 'text-lg font-semibold',
      small: 'text-sm font-medium leading-none',
      muted: 'text-sm text-muted-foreground',
      caption: 'text-xs text-muted-foreground',

      // Code and inline elements
      code: 'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold',
      kbd: 'pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100',

      // Lists
      list: 'my-6 ml-6 list-disc [&>li]:mt-2',
      'list-ordered': 'my-6 ml-6 list-decimal [&>li]:mt-2',

      // Blockquote
      blockquote: 'mt-6 border-l-2 pl-6 italic',

      // Table elements
      'table-header': 'text-xs font-medium text-muted-foreground uppercase tracking-wider',
      'table-cell': 'text-sm',

      // Card elements
      'card-title': 'text-lg font-semibold leading-none tracking-tight',
      'card-description': 'text-sm text-muted-foreground',

      // Dashboard specific
      'metric-title': 'text-sm font-medium text-muted-foreground',
      'metric-value': 'text-2xl font-bold tabular-nums',
      'metric-value-lg': 'text-3xl font-bold tabular-nums',
      'metric-subtitle': 'text-xs text-muted-foreground',
      'chart-title': 'text-lg font-semibold',
      'chart-description': 'text-sm text-muted-foreground',
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
      extrabold: 'font-extrabold',
    },
    size: {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
      '5xl': 'text-5xl',
      '6xl': 'text-6xl',
    },
    color: {
      default: '',
      muted: 'text-muted-foreground',
      primary: 'text-primary',
      secondary: 'text-secondary-foreground',
      destructive: 'text-destructive',
      success: 'text-green-700 dark:text-green-400',
      warning: 'text-yellow-700 dark:text-yellow-400',
    },
  },
  defaultVariants: {
    variant: 'body',
    color: 'default',
  },
});

interface TypographyProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'>,
    VariantProps<typeof typographyVariants> {
  as?: React.ElementType;
  asChild?: boolean;
}

function Typography({
  className,
  variant,
  weight,
  size,
  color,
  as,
  asChild = false,
  ...props
}: TypographyProps) {
  const Comp = asChild ? Slot : as || getDefaultElement(variant ?? undefined);

  return (
    <Comp
      className={cn(typographyVariants({ variant, weight, size, color }), className)}
      {...props}
    />
  );
}

function getDefaultElement(variant?: string): React.ElementType {
  switch (variant) {
    case 'h1':
      return 'h1';
    case 'h2':
      return 'h2';
    case 'h3':
      return 'h3';
    case 'h4':
      return 'h4';
    case 'h5':
      return 'h5';
    case 'h6':
      return 'h6';
    case 'p':
    case 'body':
    case 'body-sm':
    case 'body-lg':
    case 'lead':
      return 'p';
    case 'code':
      return 'code';
    case 'kbd':
      return 'kbd';
    case 'list':
      return 'ul';
    case 'list-ordered':
      return 'ol';
    case 'blockquote':
      return 'blockquote';
    default:
      return 'span';
  }
}

export { Typography, typographyVariants, type TypographyProps };
